// src/contexts/AttachmentPreviewContext.tsx - Aperçu des pièces jointes dans un Drawer

import React, { createContext, useContext, useState, useCallback } from 'react';
import { Drawer, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useMediaQuery } from '../hooks/useMediaQuery';

type OpenPreviewFn = (url: string) => void;

const AttachmentPreviewContext = createContext<OpenPreviewFn | null>(null);

function getExtension(url: string): string {
  try {
    const path = new URL(url).pathname;
    const ext = path.split('.').pop()?.toLowerCase() || '';
    return ext.split('?')[0];
  } catch {
    return '';
  }
}

function isImage(ext: string): boolean {
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
}

function isPdf(ext: string): boolean {
  return ext === 'pdf';
}

export function AttachmentPreviewProvider({ children }: { children: React.ReactNode }) {
  const [visible, setVisible] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  // 100% sur tablette et mobile, 50% sur ordinateur
  const isTabletOrMobile = useMediaQuery('(max-width: 1024px)');
  const drawerWidth = isTabletOrMobile ? '100%' : '50%';

  const openPreview = useCallback((u: string) => {
    const trimmed = u?.trim();
    if (!trimmed) return;
    setUrl(trimmed);
    setVisible(true);
  }, []);

  const closePreview = useCallback(() => {
    setVisible(false);
    setUrl(null);
  }, []);

  const ext = url ? getExtension(url) : '';
  const showImage = isImage(ext);
  const showPdf = isPdf(ext);

  return (
    <AttachmentPreviewContext.Provider value={openPreview}>
      {children}
      <Drawer
        title="Aperçu du justificatif"
        placement="right"
        width={drawerWidth}
        open={visible}
        onClose={closePreview}
        destroyOnClose
        footer={
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            href={url || '#'}
            target="_blank"
            rel="noopener noreferrer"
          >
            Ouvrir dans un nouvel onglet
          </Button>
        }
      >
        {url && (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {showImage && (
              <img
                src={url}
                alt="Justificatif"
                style={{
                  maxWidth: '100%',
                  maxHeight: 'calc(100vh - 180px)',
                  objectFit: 'contain',
                  margin: '0 auto',
                }}
              />
            )}
            {showPdf && (
              <iframe
                src={url}
                title="Justificatif PDF"
                style={{
                  width: '100%',
                  height: 'calc(100vh - 180px)',
                  border: 'none',
                }}
              />
            )}
            {!showImage && !showPdf && (
              <div style={{ padding: 24, textAlign: 'center' }}>
                <p style={{ marginBottom: 16, color: '#666' }}>
                  Aperçu non disponible pour ce type de fichier.
                </p>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Télécharger / Ouvrir
                </Button>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </AttachmentPreviewContext.Provider>
  );
}

export function useAttachmentPreview(): OpenPreviewFn | null {
  return useContext(AttachmentPreviewContext);
}
